/**
 * ClientDriveExplorer — Explorador de archivos de Google Drive para el cliente.
 * 
 * Permite:
 * 1. Vincular o crear automáticamente la carpeta del cliente en Drive.
 * 2. Navegar por carpetas (breadcrumbs).
 * 3. Previsualizar y abrir archivos directamente en Google Drive.
 */

import React, { useEffect, useState } from 'react';
import {
  HardDrive,
  Folder,
  FileText,
  FileImage,
  FileVideo,
  FileSpreadsheet,
  File as FileIcon,
  ExternalLink,
  RefreshCw,
  FolderPlus,
  Link as LinkIcon,
  ChevronRight,
  ArrowLeft,
  AlertCircle,
  Loader2,
  CheckCircle2,
  FolderOpen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatDate, formatFileSize } from '@/lib/utils';
import type { Client } from '@/types';

interface DriveFileItem {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime?: string;
  webViewLink?: string;
  iconLink?: string;
  thumbnailLink?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

interface Props {
  client: Client;
  onUpdateClient: (updatedClient: Client) => void;
}

export function ClientDriveExplorer({ client, onUpdateClient }: Props) {
  const [folderIdInput, setFolderIdInput] = useState(client.google_drive_folder_id || '');
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Explorador
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(
    client.google_drive_folder_id || null
  );
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: client.google_drive_folder_id || '', name: 'Carpeta Principal' },
  ]);
  const [files, setFiles] = useState<DriveFileItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Sincronizar input cuando cambia el cliente
  useEffect(() => {
    if (client.google_drive_folder_id) {
      setFolderIdInput(client.google_drive_folder_id);
      if (!currentFolderId) {
        setCurrentFolderId(client.google_drive_folder_id);
        setBreadcrumbs([{ id: client.google_drive_folder_id, name: 'Carpeta Principal' }]);
      }
    }
  }, [client.google_drive_folder_id]);

  // Cargar archivos cuando cambia la carpeta actual
  useEffect(() => {
    if (currentFolderId) {
      loadFiles(currentFolderId);
    } else {
      setFiles([]);
    }
  }, [currentFolderId]);

  const loadFiles = async (folderId: string) => {
    setLoadingFiles(true);
    setErrorMessage(null);
    try {
      const res = await api.getClientDriveFiles(client.id, folderId);
      if (res.error) {
        setErrorMessage(res.error);
      } else {
        setFiles(res.files || []);
      }
    } catch (err: any) {
      console.error('Error cargando archivos de Drive:', err);
      setErrorMessage(
        err.message || 'No se pudieron cargar los archivos. Verifica que Google Drive esté conectado.'
      );
    } finally {
      setLoadingFiles(false);
    }
  };

  // Crear estructura automática
  const handleCreateStructure = async () => {
    setCreatingFolder(true);
    setErrorMessage(null);
    try {
      const res = await api.createClientDriveFolder(client.id);
      if (res.success && res.clientFolderId) {
        const updated = { ...client, google_drive_folder_id: res.clientFolderId };
        onUpdateClient(updated);
        setFolderIdInput(res.clientFolderId);
        setCurrentFolderId(res.clientFolderId);
        setBreadcrumbs([{ id: res.clientFolderId, name: `Cliente_${client.name}` }]);
        await loadFiles(res.clientFolderId);
      }
    } catch (err: any) {
      console.error('Error creando carpeta en Drive:', err);
      setErrorMessage(
        err.message || 'No se pudo crear la carpeta. Asegúrate de tener Google Drive conectado.'
      );
    } finally {
      setCreatingFolder(false);
    }
  };

  // Guardar link / ID manual
  const handleSaveLink = async () => {
    setSavingLink(true);
    setErrorMessage(null);
    try {
      const res = await api.linkClientDriveFolder(client.id, folderIdInput);
      if (res.success) {
        const updated = { ...client, google_drive_folder_id: res.folderId };
        onUpdateClient(updated);
        setCurrentFolderId(res.folderId);
        setIsEditingLink(false);
        if (res.folderId) {
          setBreadcrumbs([{ id: res.folderId, name: 'Carpeta Principal' }]);
          await loadFiles(res.folderId);
        } else {
          setFiles([]);
        }
      }
    } catch (err: any) {
      console.error('Error guardando link de Drive:', err);
      setErrorMessage(err.message || 'Error vinculando la carpeta');
    } finally {
      setSavingLink(false);
    }
  };

  // Navegar a una subcarpeta
  const handleOpenFolder = (folder: DriveFileItem) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  // Navegar por breadcrumbs
  const handleNavigateBreadcrumb = (index: number) => {
    const target = breadcrumbs[index];
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
    setCurrentFolderId(target.id);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/vnd.google-apps.folder') {
      return <Folder className="w-5 h-5 text-amber-500 fill-amber-500/20 shrink-0" />;
    }
    if (mimeType.startsWith('image/')) {
      return <FileImage className="w-5 h-5 text-emerald-500 shrink-0" />;
    }
    if (mimeType.startsWith('video/')) {
      return <FileVideo className="w-5 h-5 text-purple-500 shrink-0" />;
    }
    if (mimeType.includes('pdf')) {
      return <FileText className="w-5 h-5 text-rose-500 shrink-0" />;
    }
    if (
      mimeType.includes('spreadsheet') ||
      mimeType.includes('excel') ||
      mimeType.includes('csv')
    ) {
      return <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />;
    }
    return <FileIcon className="w-5 h-5 text-slate-400 shrink-0" />;
  };

  const isFolder = (file: DriveFileItem) =>
    file.mimeType === 'application/vnd.google-apps.folder';

  const driveFolderUrl = client.google_drive_folder_id
    ? `https://drive.google.com/drive/folders/${currentFolderId || client.google_drive_folder_id}`
    : null;

  return (
    <div className="space-y-6">
      {/* ─── Tarjeta Superior: Estado y Acciones Rápidas ─── */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
                <HardDrive className="w-6 h-6" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Google Drive de {client.name}
                  {client.google_drive_folder_id ? (
                    <Badge variant="outline" className="text-emerald-700 bg-emerald-50 border-emerald-200 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Vinculado
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 bg-amber-50 border-amber-200 text-xs">
                      Sin vincular
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs text-slate-500 mt-0.5">
                  Gestiona carpetas organizadas por mes, entregables, videos y reportes sincronizados.
                </CardDescription>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {driveFolderUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(driveFolderUrl, '_blank')}
                  className="gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 font-medium text-xs shadow-sm"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  Abrir en Google Drive
                  <ExternalLink className="w-3 h-3 text-blue-400 ml-0.5" />
                </Button>
              )}

              {!client.google_drive_folder_id ? (
                <Button
                  size="sm"
                  onClick={handleCreateStructure}
                  disabled={creatingFolder}
                  className="gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm text-xs"
                >
                  {creatingFolder ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <FolderPlus className="w-3.5 h-3.5" />
                  )}
                  Crear Estructura en Drive
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditingLink(!isEditingLink)}
                  className="gap-1.5 text-xs text-slate-600 hover:text-slate-900"
                >
                  <LinkIcon className="w-3.5 h-3.5" />
                  {isEditingLink ? 'Cancelar' : 'Cambiar Carpeta'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {/* Formulario para vincular manualmente link o ID */}
        {(isEditingLink || !client.google_drive_folder_id) && (
          <CardContent className="pt-0 border-t border-slate-100 bg-slate-50/50 p-4 mt-2">
            <div className="max-w-2xl space-y-2">
              <label className="text-xs font-semibold text-slate-700 block">
                Enlace o ID de Carpeta de Google Drive:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="https://drive.google.com/drive/folders/1abc... o pega el ID"
                  value={folderIdInput}
                  onChange={(e) => setFolderIdInput(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-xs border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <Button
                  size="sm"
                  onClick={handleSaveLink}
                  disabled={savingLink}
                  className="text-xs bg-slate-800 hover:bg-slate-900 text-white shrink-0"
                >
                  {savingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Guardar Enlace'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500">
                Puedes pegar el enlace directo que copias de tu navegador en Google Drive. El sistema detectará el ID automáticamente.
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* ─── Mensaje de Error / Alerta ─── */}
      {errorMessage && (
        <div className="flex items-center gap-3 p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="flex-1">{errorMessage}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => currentFolderId && loadFiles(currentFolderId)}
            className="text-rose-700 hover:bg-rose-100 h-7 text-xs"
          >
            Reintentar
          </Button>
        </div>
      )}

      {/* ─── Explorador de Archivos ─── */}
      {client.google_drive_folder_id ? (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          {/* Barra de navegación de carpetas (Breadcrumbs) */}
          <div className="bg-slate-50/80 border-b border-slate-200 px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-xs text-slate-600 overflow-x-auto py-0.5">
              {breadcrumbs.length > 1 && (
                <button
                  onClick={() => handleNavigateBreadcrumb(breadcrumbs.length - 2)}
                  className="p-1 hover:bg-slate-200 rounded text-slate-500 mr-1"
                  title="Volver atrás"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                </button>
              )}
              {breadcrumbs.map((crumb, idx) => {
                const isLast = idx === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={crumb.id || idx}>
                    <button
                      onClick={() => !isLast && handleNavigateBreadcrumb(idx)}
                      disabled={isLast}
                      className={`hover:underline flex items-center gap-1 font-medium ${
                        isLast ? 'text-slate-900 font-semibold' : 'text-blue-600'
                      }`}
                    >
                      {idx === 0 ? <HardDrive className="w-3.5 h-3.5" /> : null}
                      {crumb.name}
                    </button>
                    {!isLast && <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                  </React.Fragment>
                );
              })}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => currentFolderId && loadFiles(currentFolderId)}
              disabled={loadingFiles}
              className="h-7 text-xs text-slate-500 hover:text-slate-800 gap-1.5"
            >
              <RefreshCw className={`w-3 h-3 ${loadingFiles ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          <CardContent className="p-0">
            {loadingFiles ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
                <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
                <p className="text-xs">Leyendo archivos de Google Drive...</p>
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-400">
                  <FolderOpen className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-semibold text-slate-700">Carpeta vacía</h4>
                <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                  Aún no hay archivos en esta carpeta. Sube tus entregables, fotos o videos en Google Drive y los verás reflejados aquí automáticamente.
                </p>
                {driveFolderUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(driveFolderUrl, '_blank')}
                    className="mt-4 gap-1.5 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Abrir carpeta para subir archivos
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {files.map((file) => {
                  const folder = isFolder(file);
                  return (
                    <div
                      key={file.id}
                      onClick={() => {
                        if (folder) {
                          handleOpenFolder(file);
                        } else if (file.webViewLink) {
                          window.open(file.webViewLink, '_blank');
                        }
                      }}
                      className="flex items-center justify-between p-3.5 hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {getFileIcon(file.mimeType)}
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate group-hover:text-blue-600 transition-colors">
                            {file.name}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                            {file.size && <span>{formatFileSize(file.size)}</span>}
                            {file.size && file.modifiedTime && <span>•</span>}
                            {file.modifiedTime && <span>Modificado: {formatDate(file.modifiedTime)}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {folder ? (
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (file.webViewLink) window.open(file.webViewLink, '_blank');
                            }}
                            className="h-7 w-7 text-slate-400 hover:text-blue-600 opacity-60 group-hover:opacity-100"
                            title="Abrir en Google Drive"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Estado cuando no tiene carpeta vinculada */
        <Card className="border border-dashed border-slate-300 bg-slate-50/50 text-center py-12 px-6">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-100">
            <HardDrive className="w-7 h-7" />
          </div>
          <h3 className="text-base font-semibold text-slate-800">
            Vincula la carpeta de Google Drive para este cliente
          </h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-6">
            Crea la estructura mensual de carpetas con un solo clic o pega el enlace de una carpeta compartida que ya uses con tu cliente.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button
              onClick={handleCreateStructure}
              disabled={creatingFolder}
              className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm text-xs"
            >
              {creatingFolder ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FolderPlus className="w-3.5 h-3.5" />
              )}
              Crear Estructura Automática
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsEditingLink(true)}
              className="text-xs gap-1.5"
            >
              <LinkIcon className="w-3.5 h-3.5" />
              Pegar Enlace Existente
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
